import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, ArrowUpRight, Target, Calendar, Flame, BadgeCheck, Users, ImageDown } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { getUserId } from "../lib/user";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import HowItWorksSection from "../components/HowItWorksSection";
import SampleJourney from "../components/SampleJourney";
import AskBoardroom from "../components/AskBoardroom";
import TaskHistoryFeed from "../components/TaskHistoryFeed";
import MinutesArtifact from "../components/MinutesArtifact";
import StreakMilestoneDialog from "../components/StreakMilestoneDialog";
import SignOffPopover from "../components/SignOffPopover";
import { markFirstSessionDone } from "../components/InstallPrompt";
import { renderStreakCard, shareStreakCard } from "../lib/streakCard";
import { sessionMoment, fmtDateTime, sessionLateness } from "../lib/dates";

function StatCard({ label, value, hint, action }) {  return (
    <div className="surface rounded-md p-6 flex flex-col gap-2" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <span className="label-eyebrow">{label}</span>
      <span className="font-serif text-4xl text-[#fafafa]">{value}</span>
      {hint && <span className="text-xs text-[#8f8f8f] font-mono">{hint}</span>}
      {action}
    </div>
  );
}

export default function Dashboard() {
  const { user: authUser } = useAuth();
  const [stats, setStats] = useState(null);
  const [meetings, setMeetings] = useState([]);
  const [shared, setShared] = useState([]);
  const [items, setItems] = useState([]);
  const [goals, setGoals] = useState([]);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cardBusy, setCardBusy] = useState(false);

  const saveStreakCard = async () => {
    setCardBusy(true);
    try {
      const blob = await renderStreakCard({
        weeks: stats?.streak_weeks ?? 0,
        chairName: authUser?.name || authUser?.email,
        onTimeRate: (stats?.scheduled_sessions_held ?? 0) >= 2 ? stats?.on_time_rate : null,
        mintedLabel: new Date().toLocaleDateString(undefined, { month: "short", year: "2-digit" }),
      });
      const how = await shareStreakCard(blob, stats?.streak_weeks ?? 0);
      if (how === "downloaded") toast.success("Card saved to your downloads.");
    } catch (e) {
      toast.error("Could not build the card.");
    } finally {
      setCardBusy(false);
    }
  };

  const load = async () => {
    try {
      const [s, m, sh, a, g, b] = await Promise.all([
        api.get("/stats"),
        api.get("/meetings"),
        api.get("/shared"),
        api.get("/action-items"),
        api.get("/goals"),
        api.get("/billing/me"),
      ]);
      setStats(s.data);
      // Once a session is on the books, the phone install nudge is welcome —
      // it surfaces on the next dashboard open, not mid-flow.
      if ((s.data?.completed_meetings ?? 0) > 0) markFirstSessionDone();
      setMeetings(m.data);
      setShared(sh.data);
      setItems(a.data);
      setGoals(g.data);
      setBilling(b.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setItemDue = async (item, date) => {
    try {
      const res = await api.patch(`/action-items/${item.id}`, { due_date: date || null });
      setItems((prev) => prev.map((p) => (p.id === item.id ? res.data : p)));
    } catch (e) {
      toast.error("Could not set due date");
    }
  };

  // Sign off + date completion in one motion. The user picks the date they
  // actually finished the work (defaults to today), and the item flips to
  // `done` server-side so it disappears from the pending list.
  const signOffWithDate = async (item, completedOn) => {
    try {
      const res = await api.post(`/action-items/${item.id}/acknowledge`, {
        completed_on: completedOn,
      });
      setItems((prev) => prev.map((p) => (p.id === item.id ? res.data : p)));
      const stamped = new Date(completedOn + "T00:00:00");
      toast.success(
        `Signed off · ${stamped.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`
      );
      // Refresh stats so the "Open actions" count on the KPI row updates
      // without a page reload.
      api.get("/stats").then((s) => setStats(s.data)).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || "Could not sign off");
    }
  };

  const recent = meetings.slice(0, 4);
  const pendingItems = items.filter((i) => i.status === "pending").slice(0, 6);
  const topGoals = goals.slice(0, 3);

  // Milestone celebration — shows once per milestone (ack is stored server-side).
  const milestone = stats?.streak_milestone;
  const showMilestone = !!milestone && !(stats?.streak_milestones_seen || []).includes(milestone);

  // Upgrade nudge logic
  const tier = billing?.effective?.tier || "free";
  const used = billing?.usage?.ai_minutes_this_month ?? 0;
  const limit = billing?.usage?.ai_minutes_limit ?? null;
  const showNudge = tier === "free" && limit !== null && used >= Math.max(1, limit - 1);

  return (
    <div className="space-y-16" data-testid="dashboard-root">
      {showMilestone && (
        <StreakMilestoneDialog
          milestone={milestone}
          chairName={authUser?.name || authUser?.email}
          onTimeRate={stats?.on_time_rate}
          onAcked={(w) => setStats((s) => ({ ...s, streak_milestones_seen: [...(s?.streak_milestones_seen || []), w] }))}
        />
      )}
      {/* Hero header — items-center when the mockup is showing (new user)
          so the artefact and headline share a vertical axis; items-end when
          the shorter stat grid replaces it (returning user), matching the
          old baseline-aligned rhythm. */}
      <section
        className={`flex flex-col lg:flex-row gap-10 justify-between ${
          (stats?.completed_meetings ?? 0) === 0 ? "lg:items-center" : "lg:items-end"
        }`}
      >
        <div className="max-w-2xl">
          <p className="label-eyebrow mb-4">Session 01 / Personal OS</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#fafafa]">
            Welcome to the <span className="text-[#c89f45]">Boardroom</span>.
          </h1>
          <p className="mt-6 text-[#aaa] text-base leading-relaxed max-w-lg">
            Run your weekly and monthly check-ins like a board meeting. Record the session,
            review the wins, name the losses, and walk out with a signed action plan.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-3">
            <Link to="/meetings/new" data-testid="dashboard-start-meeting" className="w-full sm:w-auto">
              <Button className="w-full sm:w-auto bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] rounded-md h-11 px-5 font-medium">
                Call session to order
                <ArrowUpRight size={16} className="ml-2" />
              </Button>
            </Link>
            {!authUser && (
              <button
                type="button"
                onClick={() => window.dispatchEvent(new CustomEvent("boardroom:open-auth", { detail: { view: "signup" } }))}
                className="w-full sm:w-auto h-11 px-5 rounded-md border border-[#c89f45]/50 text-[#c89f45] hover:bg-[#c89f45]/10 text-sm font-medium transition-colors"
                data-testid="dashboard-create-account"
              >
                Create a free account
              </button>
            )}
            <Link to="/meetings" data-testid="dashboard-view-history" className="w-full sm:w-auto">
              <Button variant="outline" className="w-full sm:w-auto border-[#2a2a2a] bg-transparent text-[#fafafa] hover:bg-[#121212] hover:text-white rounded-md h-11 px-5">
                Session archive
              </Button>
            </Link>
          </div>
        </div>
        {(stats?.completed_meetings ?? 0) === 0 ? (
          <div className="flex justify-center lg:justify-end w-full lg:w-[340px] lg:shrink-0">
            <MinutesArtifact testId="hero-minutes-artifact" />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3 sm:gap-4 w-full lg:w-auto lg:min-w-[320px]">
            <StatCard
              label="Sessions held"
              value={stats?.completed_meetings ?? 0}
              hint={
                (stats?.total_meetings ?? 0) > (stats?.completed_meetings ?? 0)
                  ? `of ${stats.total_meetings} on the books`
                  : "all on the record"
              }
            />
            <StatCard
              label="Open actions"
              value={stats?.pending_action_items ?? 0}
              hint={
                (stats?.signed_off_this_month ?? 0) > 0
                  ? `${stats.signed_off_this_month} signed off this month`
                  : `${stats?.completed_action_items ?? 0} cleared`
              }
            />
            <StatCard label="Active goals" value={stats?.active_goals ?? 0} hint="In flight" />
            <StatCard
              label="Streak"
              value={`${stats?.streak_weeks ?? 0}w`}
              hint={
                stats?.uses_kickoffs
                  ? (stats?.ritual_streak_weeks ?? 0) > 0
                    ? `Full ritual · ${stats.ritual_streak_weeks}w running`
                    : "Hold a kickoff + review in one week for the full ritual"
                  : (stats?.scheduled_sessions_held ?? 0) >= 2 && stats?.on_time_rate != null
                    ? `${stats.on_time_rate}% held on time`
                    : (stats?.streak_weeks ?? 0) > 0
                      ? `${stats.streak_weeks} week${stats.streak_weeks === 1 ? "" : "s"} running`
                      : "Hold a session this week"
              }
              action={
                (stats?.streak_weeks ?? 0) > 0 ? (
                  <button
                    type="button"
                    onClick={saveStreakCard}
                    disabled={cardBusy}
                    className="mt-1 inline-flex items-center gap-1.5 self-start min-h-[40px] text-xs text-[#c89f45] hover:underline disabled:opacity-60"
                    data-testid="streak-card-download"
                  >
                    <ImageDown size={13} /> {cardBusy ? "Making the card…" : "Share card"}
                  </button>
                ) : null
              }
            />
          </div>
        )}
      </section>

      {(stats?.completed_meetings ?? 0) === 0 && <HowItWorksSection />}
      {(stats?.completed_meetings ?? 0) === 0 && <SampleJourney />}

      {(stats?.completed_meetings ?? 0) > 0 && <QuarterReviewCard />}

      {!loading && (
        <AskBoardroom hasRecord={meetings.some((m) => m.transcript || m.minutes || (m.notes && Object.values(m.notes).some(Boolean)))} />
      )}

      {!loading && <TaskHistoryFeed />}

      <div className="gold-divider" />

      {showNudge && (
        <Link to="/pricing" className="block surface surface-hover rounded-md p-5 border-[#c89f45]/40 transition-colors" data-testid="upgrade-nudge">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="label-eyebrow text-[#c89f45]">{used >= limit ? "You're out of AI minutes" : "Heads up"}</p>
              <p className="font-serif text-xl mt-2">
                {used >= limit
                  ? "You've used all 3 free AI meeting minutes this month."
                  : `You've used ${used} of ${limit} free AI minutes this month.`}
              </p>
              <p className="text-xs text-[#888] mt-1">Upgrade to Pro for unlimited minutes, partners, reminders, and live calls — 7-day free trial.</p>
            </div>
            <ArrowUpRight size={18} className="text-[#c89f45] shrink-0" />
          </div>
        </Link>
      )}

      {/* Recent sessions + action items */}
      <section className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 space-y-6">
          <div className="flex items-end justify-between">
            <div>
              <p className="label-eyebrow">Minutes / archive</p>
              <h2 className="font-serif text-2xl sm:text-3xl mt-2">Recent sessions</h2>
            </div>
            <Link to="/meetings" className="text-xs text-[#888] hover:text-[#c89f45] transition-colors" data-testid="see-all-meetings">
              See all →
            </Link>
          </div>

          {loading ? (
            <div className="surface rounded-md p-8 text-sm text-[#8a8a8a]">Loading sessions…</div>
          ) : recent.length === 0 ? (
            <div className="surface rounded-md p-10 text-center" data-testid="empty-meetings">
              <p className="font-serif text-xl">No sessions on the record.</p>
              <p className="text-sm text-[#888] mt-2">Start your first board meeting — solo or with a partner.</p>
              <Link to="/meetings/new">
                <Button className="mt-6 bg-[#c89f45] hover:bg-[#b48c34] text-[#0a0a0a] rounded-md">
                  <Plus size={14} className="mr-1.5" /> First session
                </Button>
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {recent.map((m) => (
                <Link
                  key={m.id}
                  to={m.status === "completed" ? `/meetings/${m.id}/minutes` : `/meetings/${m.id}`}
                  className="surface surface-hover rounded-md p-5 flex items-center justify-between transition-colors block"
                  data-testid={`meeting-card-${m.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3">
                      <span className="label-eyebrow">{m.cadence} · {m.meeting_type}</span>
                      <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded ${
                        m.status === "completed" ? "bg-[#1B3B36] text-[#9bd3c4]" :
                        m.status === "active" ? "bg-[#c89f45]/20 text-[#c89f45]" :
                        "bg-[#1a1a1a] text-[#888]"
                      }`}>{m.status}</span>
                    </div>
                    <h3 className="font-serif text-xl mt-2 truncate">{m.title}</h3>
                    <p className="text-xs text-[#8a8a8a] mt-1 font-mono">
                      {(() => { const w = sessionMoment(m); return `${w.label.toUpperCase()} · ${fmtDateTime(w.iso)}`; })()}
                      {sessionLateness(m) && <span className="text-[#8f8266]">{` · ${sessionLateness(m).label}`}</span>}
                    </p>
                  </div>
                  <ArrowUpRight size={18} className="text-[#8a8a8a] ml-4 shrink-0" />
                </Link>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-5 space-y-6">
          <div className="flex items-end justify-between">
            <div>
              <p className="label-eyebrow">Resolutions</p>
              <h2 className="font-serif text-2xl sm:text-3xl mt-2">Action items</h2>
            </div>
            <span className="text-xs text-[#8a8a8a] font-mono flex flex-col items-end gap-0.5">
              <span data-testid="action-items-open-count">{pendingItems.length} OPEN</span>
              {(stats?.signed_off_this_month ?? 0) > 0 && (
                <span
                  className="text-[10px] text-[#c89f45]"
                  data-testid="action-items-signed-off-count"
                >
                  {stats.signed_off_this_month} SIGNED OFF · THIS MONTH
                </span>
              )}
            </span>
          </div>

          {pendingItems.length === 0 ? (
            <div className="surface rounded-md p-8 text-sm text-[#8a8a8a]" data-testid="empty-actions">
              The floor is clear. Resolutions appear here once a session ends.
            </div>
          ) : (
            <ul className="space-y-2">
              {pendingItems.map((item) => {
                const me = getUserId();
                const acks = item.acknowledgements || [];
                const isShared = item.owner_id !== me;
                return (
                <li
                  key={item.id}
                  className="surface rounded-md p-4 flex items-start gap-3 group hover:border-[#3a3a3a] transition-colors"
                  data-testid={`action-item-${item.id}`}
                >
                  {/* Gold gavel bullet — visually anchors each resolution without
                      pretending to be an interactive checkbox. The old checkbox
                      is gone; completion happens via "Sign off" + a date. */}
                  <span
                    aria-hidden
                    className="mt-1.5 h-1.5 w-1.5 rounded-full bg-[#c89f45]/60 shrink-0"
                    data-testid={`action-marker-${item.id}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm">{item.title}</p>
                      {isShared && (
                        <span className="inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-[#c89f45] border border-[#c89f45]/40 rounded px-1.5 py-0.5" data-testid={`shared-badge-${item.id}`}>
                          <Users size={10} /> SHARED
                        </span>
                      )}
                    </div>
                    {item.description && <p className="text-xs text-[#8a8a8a] mt-1">{item.description}</p>}
                    <div className="flex flex-wrap items-center gap-3 mt-2">
                      <input
                        type="date"
                        value={item.due_date ? item.due_date.slice(0, 10) : ""}
                        onChange={(e) => setItemDue(item, e.target.value)}
                        disabled={isShared}
                        className="bg-transparent text-[10px] font-mono text-[#888] border-0 p-0 focus:outline-none focus:text-[#c89f45] disabled:opacity-50"
                        data-testid={`action-due-${item.id}`}
                      />
                      {item.due_date && (
                        <span className="text-[10px] font-mono text-[#8a8a8a]">
                          {(() => {
                            const d = new Date(item.due_date);
                            const today = new Date(); today.setHours(0,0,0,0);
                            const day = new Date(d); day.setHours(0,0,0,0);
                            const diff = Math.round((day - today) / 86400000);
                            if (diff < 0) return `${Math.abs(diff)}D OVERDUE`;
                            if (diff === 0) return "DUE TODAY";
                            if (diff === 1) return "DUE TOMORROW";
                            return `IN ${diff}D`;
                          })()}
                        </span>
                      )}
                      {acks.length > 0 && (
                        <div className="flex items-center gap-1" data-testid={`acks-${item.id}`}>
                          <BadgeCheck size={11} className="text-[#c89f45]" />
                          <span className="text-[10px] font-mono text-[#888]">
                            SIGNED · {acks.map((a) => a.name || "Member").join(", ")}
                          </span>
                        </div>
                      )}
                      <SignOffPopover
                        item={item}
                        onConfirm={(date) => signOffWithDate(item, date)}
                      />
                    </div>
                  </div>
                </li>
              );})}
            </ul>
          )}
        </div>
      </section>

      {/* Shared with you */}
      {shared.length > 0 && (
        <section data-testid="shared-section">
          <div className="flex items-end justify-between mb-6">
            <div>
              <p className="label-eyebrow">Partner sessions</p>
              <h2 className="font-serif text-2xl sm:text-3xl mt-2">Shared with you</h2>
            </div>
            <span className="text-xs text-[#8a8a8a] font-mono">{shared.length} BOARD{shared.length === 1 ? "" : "S"}</span>
          </div>
          <div className="space-y-3">
            {shared.map((m) => (
              <Link
                key={m.id}
                to={m.status === "active" ? `/live/${m.id}` : `/meetings/${m.id}/minutes`}
                className="surface surface-hover rounded-md p-5 flex items-center justify-between transition-colors block"
                data-testid={`shared-row-${m.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <span className="label-eyebrow">{m.cadence} · partner-board</span>
                    <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded ${
                      m.status === "active" ? "bg-[#c89f45]/20 text-[#c89f45]" :
                      m.status === "completed" ? "bg-[#1B3B36] text-[#9bd3c4]" : "bg-[#1a1a1a] text-[#888]"
                    }`}>{m.status === "active" ? "live now" : m.status}</span>
                  </div>
                  <h3 className="font-serif text-xl mt-2 truncate">{m.title}</h3>
                  <p className="text-xs text-[#8a8a8a] mt-1 font-mono">
                    {(() => { const w = sessionMoment(m); return `${w.label.toUpperCase()} · ${fmtDateTime(w.iso)}`; })()}
                  </p>
                </div>
                <ArrowUpRight size={18} className="text-[#8a8a8a] ml-4 shrink-0" />
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Goals */}
      <section>
        <div className="flex items-end justify-between mb-6">
          <div>
            <p className="label-eyebrow">Strategy</p>
            <h2 className="font-serif text-2xl sm:text-3xl mt-2">Active goals</h2>
          </div>
          <Link to="/goals" className="text-xs text-[#888] hover:text-[#c89f45] transition-colors" data-testid="see-all-goals">
            Manage →
          </Link>
        </div>

        {topGoals.length === 0 ? (
          <div className="surface rounded-md p-10 text-center" data-testid="empty-goals">
            <Target size={20} className="mx-auto text-[#c89f45] mb-3" />
            <p className="font-serif text-xl">No goals on the table.</p>
            <p className="text-sm text-[#888] mt-2">Set the agenda — what are you holding yourself to this month?</p>
            <Link to="/goals">
              <Button className="mt-6 bg-transparent border border-[#c89f45] text-[#c89f45] hover:bg-[#c89f45] hover:text-[#0a0a0a] rounded-md">
                Define a goal
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {topGoals.map((g) => (
              <div key={g.id} className="surface rounded-md p-5" data-testid={`goal-card-${g.id}`}>
                <div className="flex items-center justify-between">
                  <span className="label-eyebrow">{g.cadence}</span>
                  <Flame size={14} className="text-[#c89f45]" />
                </div>
                <h3 className="font-serif text-xl mt-3">{g.title}</h3>
                {g.description && <p className="text-xs text-[#888] mt-2">{g.description}</p>}
                <div className="mt-5">
                  <div className="h-px bg-[#2a2a2a] relative">
                    <div className="absolute left-0 top-0 h-px bg-[#c89f45]" style={{ width: `${g.progress}%` }} />
                  </div>
                  <div className="flex justify-between mt-2 text-[10px] font-mono text-[#8a8a8a]">
                    <span>{g.progress}%</span>
                    <span>{g.target_date ? new Date(g.target_date).toLocaleDateString() : "open"}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

/**
 * Quarter Review prompt — surfaces the most recent quarter that has sessions,
 * compiled or not, so the artefact is one tap from the dashboard.
 */
function QuarterReviewCard() {
  const [q, setQ] = useState(null);

  useEffect(() => {
    api.get("/quarter-reviews")
      .then((r) => setQ((r.data.quarters || [])[0] || null))
      .catch(() => {});
  }, []);

  if (!q) return null;

  return (
    <section>
      <Link
        to={`/quarter-review/${q.year}/${q.quarter}`}
        className="surface rounded-md p-5 sm:p-6 flex items-center justify-between gap-4 sm:gap-6 hover:border-[#c89f45]/40 transition-colors group"
        data-testid="dashboard-quarter-review-card"
      >
        <div>
          <p className="label-eyebrow">
            {q.compiled_at ? "Board Book · compiled" : "Board Book · ready to compile"}
          </p>
          <h2 className="font-serif text-2xl mt-2 group-hover:text-[#c89f45] transition-colors">
            Q{q.quarter} {q.year} review
          </h2>
          <p className="text-sm text-[#8f8f8f] mt-2">
            {q.sessions_held} session{q.sessions_held === 1 ? "" : "s"} on the record
            {q.compiled_at ? " · open the Board Book" : " · roll them into one review"}
          </p>
        </div>
        <ArrowUpRight size={18} className="text-[#8a8a8a] group-hover:text-[#c89f45] transition-colors shrink-0" />
      </Link>
    </section>
  );
}
